'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect } from 'react'
import posthog from 'posthog-js'
import { onConsentGranted } from '@/lib/consent/client'
import { CLICK_CONTEXT_PARAM, decodeClickContextUnverified } from '@/lib/click-context-decode'

// STRICT: Only fridge.com - NO localhost, NO preview deployments
const isProduction = typeof window !== 'undefined' &&
  window.location.hostname === 'fridge.com'

type SearchParamsLike = { toString(): string; get(name: string): string | null } | null

/**
 * PostHogPageView - FULL SITE TRACKING
 *
 * Tracks ALL page types on fridge.com:
 * - Homepage
 * - Collection Pages (50k+) with pagination
 * - Product Pages (1k+)
 * - Blog Home
 * - Blog Post Pages (17k+) with pagination
 * - Blog Tag Pages (750+) with pagination
 * - Tools Home
 * - Tools (17 live)
 * - Static Pages (about, contact, privacy, terms, faq, sitemap)
 * - ALL pagination pages
 *
 * NO localhost, NO preview deployments - production only!
 *
 * FRIDGE-115: for consent-gated visitors PostHog is not initialised until
 * they choose Allow (app/providers.tsx), and posthog.capture() before init
 * is a silent no-op — so the page they were on when they allowed would never
 * be counted. The second effect below re-captures the current page once
 * consent lands. It runs on the next macrotask because the provider's init
 * listener and this one fire in the same dispatch and React registers the
 * child's effect first.
 */
export default function PostHogPageView() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    // STRICT: Only track in production (fridge.com)
    if (!isProduction) return
    capturePageview(pathname, searchParams)
  }, [pathname, searchParams])

  useEffect(() => {
    if (!isProduction) return
    return onConsentGranted(() => {
      setTimeout(() => capturePageview(pathname, searchParams), 0)
    })
  }, [pathname, searchParams])

  return null
}

function capturePageview(pathname: string | null, searchParams: SearchParamsLike) {
  if (pathname && posthog) {
    let url = window.origin + pathname
    const queryString = searchParams?.toString() || ''
    if (queryString) {
      url = url + `?${queryString}`
    }
    
    // Extract pagination info
    const pageNumber = searchParams?.get('page') ? parseInt(searchParams.get('page')!, 10) : 1
    const isPaginated = pageNumber > 1
    
    // Determine page type and extract metadata
    let pageType = 'static' // Default to static instead of 'other'
    let pageData: Record<string, any> = {
      page_number: pageNumber,
      is_paginated: isPaginated,
    }
    
    // === HOMEPAGE ===
    if (pathname === '/') {
      pageType = 'homepage'
    }
    
    // === PRODUCTS INDEX ===
    else if (pathname === '/products') {
      pageType = 'products_home'
    }
    
    // === PRODUCT PAGES (1k+) ===
    else if (pathname.startsWith('/products/')) {
      pageType = 'product'
      pageData.product_slug = pathname.replace('/products/', '')
    }
    
    // === COLLECTION PAGES (50k+ including pagination) ===
    else if (pathname.startsWith('/collections/')) {
      pageType = 'collection'
      const collectionSlug = pathname.replace('/collections/', '')
      pageData.collection_slug = collectionSlug
    }
    
    // === BLOG HOME ===
    else if (pathname === '/blogs' || pathname === '/blogs/news') {
      pageType = 'blog_home'
    }
    
    // === BLOG TAGS INDEX ===
    else if (pathname === '/blogs/news/tags') {
      pageType = 'blog_tags_home'
    }
    
    // === BLOG TAG PAGES (750+ including pagination) ===
    else if (pathname.startsWith('/blogs/news/tags/')) {
      pageType = 'blog_tag'
      const tagSlug = pathname.replace('/blogs/news/tags/', '')
      pageData.tag_slug = tagSlug
    }
    
    // === BLOG POST PAGES (17k+) ===
    else if (pathname.startsWith('/blogs/news/')) {
      pageType = 'blog_post'
      pageData.article_slug = pathname.replace('/blogs/news/', '')
    }
    
    // === TOOLS HOME ===
    else if (pathname === '/tools') {
      pageType = 'tools_home'
    }
    
    // === INDIVIDUAL TOOLS (17 live) ===
    else if (pathname.startsWith('/tools/')) {
      pageType = 'tool'
      pageData.tool_slug = pathname.replace('/tools/', '')
    }
    
    // === STATIC PAGES ===
    else if (pathname === '/about-us') {
      pageType = 'static_about'
    }
    else if (pathname === '/contact') {
      pageType = 'static_contact'
    }
    else if (pathname === '/privacy-policy') {
      pageType = 'static_privacy'
    }
    else if (pathname === '/terms-of-service') {
      pageType = 'static_terms'
    }
    else if (pathname === '/faq') {
      pageType = 'static_faq'
    }
    else if (pathname === '/sitemap') {
      pageType = 'static_sitemap'
    }
    
    // FRIDGE-818: a visit that began in the ChatGPT App arrives with a signed
    // `?cx=` click context. Decoded here UNVERIFIED (the page has no secret)
    // so the funnel's "visit" step carries the channel; the /r/ click row is
    // the verified record.
    const ctx = decodeClickContextUnverified(searchParams?.get(CLICK_CONTEXT_PARAM))
    if (ctx) {
      pageData.channel = ctx.channel
      pageData.channel_surface = ctx.surface
      pageData.channel_tool = ctx.tool
      pageData.channel_position = ctx.position ?? undefined
      pageData.channel_fin = ctx.fin ?? undefined
      pageData.channel_trace = ctx.trace ?? undefined
      pageData.channel_verified = false
    }

    // Capture pageview with full metadata (person properties included in event, not set separately)
    // Removed setPersonProperties call - it was running on EVERY navigation and hurting INP
    posthog.capture('$pageview', {
      $current_url: url,
      page_type: pageType,
      ...pageData,
    })
  }
}
